import { FC } from 'react';
import { Box, Typography, Button } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { DataAccessProvider } from '@constants/constants';

interface DataSourceOverviewProps {
  source: DataAccessProvider;
  data: Record<string, string | string[] | number | boolean>;
}

// Interface for Data Source Item structure
interface DataSourceItem {
  label: string;
  key: string;
  type?: string;
}

// Interface for Data Source information structure
interface DataSourceType {
  [key: string]: DataSourceItem[];
}

// Configuration for data integration fields display
const DATA_INTEGRATION_FIELDS: DataSourceType = {
  APPS_FLYER: [
    { label: 'Data Source', key: 'dataSource' },
    { label: 'Account Name', key: 'accountName' },
    {
      label: 'Margin on IAP Revenue',
      key: 'iapRevenueMargin',
      type: 'percentage',
    },
    // { label: "API Token", key: "apiToken" },
    {
      label: 'Monthly Refresh',
      key: 'monthlyRefresh',
      type: 'boolean',
    },
  ],
  ADJUST: [
    { label: 'Data Source', key: 'dataSource' },
    { label: 'Account Name', key: 'accountName' },
    {
      label: 'Margin on IAP Revenue',
      key: 'iapRevenueMargin',
      type: 'percentage',
    },
    { label: 'Ad Revenue Sources', key: 'iaaIdList', type: 'array' },
    {
      label: 'Monthly Refresh',
      key: 'monthlyRefresh',
      type: 'boolean',
    },
  ],
  BIGQUERY: [
    { label: 'Data Source', key: 'dataSource' },
    { label: 'IAP Transactions Table Name', key: 'iapTransactionsTableName' },
    { label: 'IAA Transactions Table Name', key: 'iaaTransactionsTableName' },
    { label: 'UA Spends Table Name', key: 'uaSpendsTableName' },
    {
      label: 'Monthly Refresh',
      key: 'monthlyRefresh',
      type: 'boolean',
    },
  ],
  GCS: [
    { label: 'Data Source', key: 'dataSource' },
    { label: 'GCS Bucket Path', key: 'gcsBucketPath' },
    { label: 'IAP File Name', key: 'iapFileName' },
    { label: 'IAA File Name', key: 'iaaFileName' },
    { label: 'UA File Name', key: 'uaFileName' },
    {
      label: 'Monthly Refresh',
      key: 'monthlyRefresh',
      type: 'boolean',
    },
  ],
  S3: [
    { label: 'Data Source', key: 'dataSource' },
    { label: 'S3 Bucket Path', key: 's3BucketPath' },
    { label: 'IAP File Name', key: 'iapFileName' },
    { label: 'IAA File Name', key: 'iaaFileName' },
    { label: 'UA File Name', key: 'uaFileName' },
    {
      label: 'Monthly Refresh',
      key: 'monthlyRefresh',
      type: 'boolean',
    },
  ],
  MYSQL: [
    { label: 'Data Source', key: 'dataSource' },
    { label: 'Host Name', key: 'hostname' },
    { label: 'Port', key: 'port' },
    { label: 'Database Name', key: 'databaseName' },
    { label: 'Username', key: 'readOnlyUsername' },
    { label: 'Password', key: 'password', type: 'password' },
    {
      label: 'Monthly Refresh',
      key: 'monthlyRefresh',
      type: 'boolean',
    },
  ],
  SNOWFLAKE: [
    { label: 'Data Source', key: 'dataSource' },
    { label: 'Username', key: 'username' },
    { label: 'Password', key: 'password', type: 'password' },
    { label: 'Host', key: 'host' },
    { label: 'Role', key: 'role' },
    { label: 'Warehouse', key: 'warehouse' },
    { label: 'Database Name', key: 'databaseName' },
    { label: 'Schema', key: 'schema' },
    {
      label: 'Monthly Refresh',
      key: 'monthlyRefresh',
      type: 'boolean',
    },
  ],
  GOOGLE_DRIVE: [
    { label: 'Data Source', key: 'dataSource' },
    { label: 'IAP File Drive Link', key: 'iapFileDriveLink' },
    { label: 'IAA File Drive Link', key: 'iaaFileDriveLink' },
    { label: 'UA Spend File Drive Link', key: 'uaSpendFileDriveLink' },
    {
      label: 'Monthly Refresh',
      key: 'monthlyRefresh',
      type: 'boolean',
    },
  ],
  AGGREGATED_DATA: [
    { label: 'Data Source', key: 'dataSource' },
    { label: 'publicUrl', key: 'publicUrl' },
    { label: 'Document (.xls, .xlsx, .csv)', key: 'signedUrl', type: 'file' },
  ],
};

const getFieldBasedOnType = (
  type: string | undefined,
  data: string | string[] | number | boolean,
) => {
  switch (type) {
    case 'array':
      return (
        <Typography fontSize="0.875rem" lineHeight="1.25rem" fontWeight={700} color="#6C6C6C">
          {Array.isArray(data)
            ? data.length > 0
              ? data.join(', ')
              : 'Not provided'
            : data || 'Not provided'}
        </Typography>
      );
    case 'percentage':
      return (
        <Typography fontSize="0.875rem" lineHeight="1.25rem" fontWeight={700} color="#6C6C6C">
          {`${(Number(data) * 100).toFixed(0)}%` || 'Not provided'}
        </Typography>
      );
    case 'password':
      return '*********';
    case 'file':
      return !data ? (
        <Typography fontSize="0.875rem" lineHeight="1.25rem" fontWeight={700} color="#6C6C6C">
          Not provided
        </Typography>
      ) : (
        <Button
          onClick={() => {
            const link = document.createElement('a');
            link.href = data as string;
            link.target = '_blank';
            link.download = '';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
          sx={{
            minWidth: '0px',
            padding: '4px 8px',
            color: '#162C36',
            fontSize: '0.875rem',
            fontWeight: 700,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'rgba(22, 44, 54, 0.04)',
            },
          }}
          startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: '1rem' }} />}
        >
          Download
        </Button>
      );
    case 'boolean':
      return (
        <Typography fontSize="0.875rem" lineHeight="1.25rem" fontWeight={700} color="#6C6C6C">
          {data ? 'Yes' : 'No'}
        </Typography>
      );
    default:
      return (
        <Typography fontSize="0.875rem" lineHeight="1.25rem" fontWeight={700} color="#6C6C6C">
          {data || 'Not provided'}
        </Typography>
      );
  }
};

const DataSourceOverview: FC<DataSourceOverviewProps> = ({ source, data }) => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '1rem',
      }}
    >
      {DATA_INTEGRATION_FIELDS?.[source]?.map(({ label, key, type: fieldType }) => (
        <Box key={key}>
          <Typography fontSize="0.75rem" lineHeight="1rem" fontWeight={700} color="#6C6C6C">
            {label}
          </Typography>

          {getFieldBasedOnType(fieldType, data?.[key as keyof typeof data])}
        </Box>
      ))}
    </Box>
  );
};

export default DataSourceOverview;
